import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import ShadingMapModal from "@/components/ShadingMapModal";
import {
  Pen, PenTool, Wand2, Lasso, Square, Circle, Move, Trash2, Eye, EyeOff,
  Layers, Save, Upload, ZoomIn, ZoomOut, Undo2, Redo2, ChevronLeft, Loader2,
  Sparkles, Copy, ClipboardPaste, Group, Ungroup, Bot, ChevronUp, ChevronDown,
} from "lucide-react";

/**
 * SurfaceEditor — Compositore visuale per Casa Madre.
 *
 * Tools: pen (magnetica) · bezier (Illustrator) · wand · lasso · rect · ellipse · move
 * Multi-select: Shift+click su layer · marquee (drag su area vuota con tool=move)
 * Gruppi: Ctrl+G raggruppa · Ctrl+Shift+G sgruppa · 1 texture per tutto il gruppo
 * Copia/Incolla: Ctrl+C / Ctrl+V (layer selezionati · offset 30px)
 * Paste immagine: incolla screenshot dal clipboard → popup "Sfondo / Nuovo layer"
 * Undo/Redo: Ctrl+Z / Ctrl+Shift+Z (storico 30 step)
 */

const TOOLS = [
  { id: "pen", icon: Pen, label: "Penna magnetica" },
  { id: "bezier", icon: PenTool, label: "Penna Bezier (Illustrator)" },
  { id: "wand", icon: Wand2, label: "Bacchetta magica" },
  { id: "lasso", icon: Lasso, label: "Lazo" },
  { id: "rect", icon: Square, label: "Rettangolo" },
  { id: "ellipse", icon: Circle, label: "Ellisse" },
  { id: "move", icon: Move, label: "Sposta / Multi-select" },
];

const SURFACE_GROUPS = [
  { id: "frame", label: "Struttura (piede)", picker_axes: ["metallo"] },
  { id: "upholstery", label: "Imbottitura (tessuto)", picker_axes: ["tessuto"] },
  { id: "leg_finish", label: "Finitura metallo", picker_axes: ["metallo"] },
];

const LAYER_COLORS = ["#ef4444", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899"];
const HISTORY_MAX = 30;

// Blend mode CSS (come Photoshop) — applicabile per singolo layer
const BLEND_MODES = [
  { id: "auto", label: "Automatico (per gruppo)" },
  { id: "normal", label: "Normale" },
  { id: "multiply", label: "Moltiplica" },
  { id: "screen", label: "Scolora" },
  { id: "overlay", label: "Sovrapponi" },
  { id: "darken", label: "Scurisci" },
  { id: "lighten", label: "Schiarisci" },
  { id: "color-dodge", label: "Scolorisci sotto" },
  { id: "color-burn", label: "Brucia colore" },
  { id: "hard-light", label: "Luce intensa" },
  { id: "soft-light", label: "Luce soffusa" },
  { id: "difference", label: "Differenza" },
  { id: "exclusion", label: "Esclusione" },
  { id: "hue", label: "Tonalità" },
  { id: "saturation", label: "Saturazione" },
  { id: "color", label: "Colore" },
  { id: "luminosity", label: "Luminosità" },
];

const QUICK_TEMPLATES = [
  {
    id: "sedia", emoji: "🪑", label: "Sedia",
    layers: [
      { name: "Gamba ant. SX", svg_path: "M380 780 L430 780 L440 1140 L370 1140 Z", surface_group: "frame", z_index: 1 },
      { name: "Gamba ant. DX", svg_path: "M770 780 L820 780 L830 1140 L760 1140 Z", surface_group: "frame", z_index: 1 },
      { name: "Gamba post. SX", svg_path: "M360 760 L405 760 L415 1140 L345 1140 Z", surface_group: "frame", z_index: 1 },
      { name: "Gamba post. DX", svg_path: "M795 760 L840 760 L855 1140 L785 1140 Z", surface_group: "frame", z_index: 1 },
      { name: "Seduta", svg_path: "M310 690 L890 690 L890 800 L310 800 Z", surface_group: "upholstery", z_index: 2 },
      { name: "Schienale", svg_path: "M340 200 L860 200 L860 690 L340 690 Z", surface_group: "upholstery", z_index: 3 },
    ],
  },
  {
    id: "poltrona", emoji: "🛋️", label: "Poltrona",
    layers: [
      { name: "Gamba ant. SX", svg_path: "M300 920 L350 920 L355 1130 L295 1130 Z", surface_group: "frame", z_index: 1 },
      { name: "Gamba ant. DX", svg_path: "M850 920 L900 920 L905 1130 L845 1130 Z", surface_group: "frame", z_index: 1 },
      { name: "Gamba post. SX", svg_path: "M280 910 L330 910 L335 1130 L275 1130 Z", surface_group: "frame", z_index: 1 },
      { name: "Gamba post. DX", svg_path: "M870 910 L920 910 L925 1130 L865 1130 Z", surface_group: "frame", z_index: 1 },
      { name: "Seduta", svg_path: "M260 760 L940 760 L940 920 L260 920 Z", surface_group: "upholstery", z_index: 2 },
      { name: "Bracciolo SX", svg_path: "M200 480 L350 480 L360 920 L210 920 Z", surface_group: "upholstery", z_index: 3 },
      { name: "Bracciolo DX", svg_path: "M850 480 L1000 480 L990 920 L840 920 Z", surface_group: "upholstery", z_index: 3 },
      { name: "Schienale", svg_path: "M320 220 L880 220 L890 760 L310 760 Z", surface_group: "upholstery", z_index: 4 },
    ],
  },
  {
    id: "sgabello", emoji: "💺", label: "Sgabello",
    layers: [
      { name: "Gamba 1", svg_path: "M470 700 L500 700 L505 1130 L465 1130 Z", surface_group: "frame", z_index: 1 },
      { name: "Gamba 2", svg_path: "M700 700 L730 700 L735 1130 L695 1130 Z", surface_group: "frame", z_index: 1 },
      { name: "Gamba 3", svg_path: "M450 720 L480 720 L485 1130 L445 1130 Z", surface_group: "frame", z_index: 1 },
      { name: "Gamba 4", svg_path: "M720 720 L750 720 L755 1130 L715 1130 Z", surface_group: "frame", z_index: 1 },
      { name: "Seduta", svg_path: "M400 620 C400 580, 800 580, 800 620 L800 720 C800 760, 400 760, 400 720 Z", surface_group: "upholstery", z_index: 2 },
    ],
  },
  {
    id: "panca", emoji: "🪵", label: "Panca",
    layers: [
      { name: "Gamba SX-A", svg_path: "M150 740 L190 740 L195 1130 L145 1130 Z", surface_group: "frame", z_index: 1 },
      { name: "Gamba DX-A", svg_path: "M1010 740 L1050 740 L1055 1130 L1005 1130 Z", surface_group: "frame", z_index: 1 },
      { name: "Gamba SX-P", svg_path: "M130 720 L170 720 L175 1130 L125 1130 Z", surface_group: "frame", z_index: 1 },
      { name: "Gamba DX-P", svg_path: "M1030 720 L1070 720 L1075 1130 L1025 1130 Z", surface_group: "frame", z_index: 1 },
      { name: "Seduta", svg_path: "M100 640 L1100 640 L1100 760 L100 760 Z", surface_group: "upholstery", z_index: 2 },
      { name: "Schienale", svg_path: "M130 400 L1070 400 L1070 640 L130 640 Z", surface_group: "upholstery", z_index: 3 },
    ],
  },
  {
    id: "tavolino", emoji: "🟫", label: "Tavolino",
    layers: [
      { name: "Gamba SX-A", svg_path: "M260 750 L295 750 L300 1120 L255 1120 Z", surface_group: "frame", z_index: 1 },
      { name: "Gamba DX-A", svg_path: "M905 750 L940 750 L945 1120 L900 1120 Z", surface_group: "frame", z_index: 1 },
      { name: "Gamba SX-P", svg_path: "M240 730 L280 730 L285 1120 L235 1120 Z", surface_group: "frame", z_index: 1 },
      { name: "Gamba DX-P", svg_path: "M920 730 L960 730 L965 1120 L915 1120 Z", surface_group: "frame", z_index: 1 },
      { name: "Piano", svg_path: "M180 600 L1020 600 L1040 750 L160 750 Z", surface_group: "frame", z_index: 2 },
    ],
  },
];

// ----- helpers ---------------------------------------------------------------

function bezierNodesToPath(nodes, closed) {
  if (!nodes || nodes.length < 2) return "";
  let d = `M${nodes[0].x} ${nodes[0].y}`;
  const segs = closed ? nodes.length : nodes.length - 1;
  for (let i = 0; i < segs; i++) {
    const a = nodes[i];
    const b = nodes[(i + 1) % nodes.length];
    if (a.hOut || b.hIn) {
      const c1 = a.hOut || { x: a.x, y: a.y };
      const c2 = b.hIn || { x: b.x, y: b.y };
      d += ` C${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${b.x} ${b.y}`;
    } else {
      d += ` L${b.x} ${b.y}`;
    }
  }
  if (closed) d += " Z";
  return d;
}

/** Trasla un path SVG (M/L/C/Q assoluti) di (dx,dy). */
function shiftSvgPath(d, dx, dy) {
  if (!d || (dx === 0 && dy === 0)) return d;
  return d.replace(/([MLCQ])\s*([^MLCQZ]+)/gi, (_, cmd, rest) => {
    const nums = rest.trim().split(/[\s,]+/).filter((s) => s !== "").map(Number);
    const shifted = nums.map((n, i) => (i % 2 === 0 ? n + dx : n + dy));
    return cmd + shifted.join(" ") + " ";
  });
}

/** Parse path → lista di comandi {cmd, nums} (assoluti M/L/C/Q/Z). */
function parsePathCmds(d) {
  if (!d) return [];
  const cmds = [];
  const re = /([MLCQZ])\s*([^MLCQZ]*)/gi;
  let m;
  while ((m = re.exec(d)) !== null) {
    const cmd = m[1].toUpperCase();
    const rest = (m[2] || "").trim();
    const nums = rest ? rest.split(/[\s,]+/).filter((s) => s !== "").map(Number) : [];
    cmds.push({ cmd, nums });
  }
  return cmds;
}

/** Estrae i punti modificabili (anchor + control handles) da un path. */
function pathEditablePoints(d) {
  const cmds = parsePathCmds(d);
  const pts = [];
  cmds.forEach((c, cmdIdx) => {
    if (c.cmd === "Z") return;
    if (c.cmd === "M" || c.cmd === "L") {
      pts.push({ type: "anchor", x: c.nums[0], y: c.nums[1], cmdIdx, numIdx: 0 });
    } else if (c.cmd === "Q") {
      pts.push({ type: "ctrl", x: c.nums[0], y: c.nums[1], cmdIdx, numIdx: 0 });
      pts.push({ type: "anchor", x: c.nums[2], y: c.nums[3], cmdIdx, numIdx: 2 });
    } else if (c.cmd === "C") {
      pts.push({ type: "ctrl", x: c.nums[0], y: c.nums[1], cmdIdx, numIdx: 0 });
      pts.push({ type: "ctrl", x: c.nums[2], y: c.nums[3], cmdIdx, numIdx: 2 });
      pts.push({ type: "anchor", x: c.nums[4], y: c.nums[5], cmdIdx, numIdx: 4 });
    }
  });
  return { cmds, points: pts };
}

/** Serializza cmds → path string. */
function serializePathCmds(cmds) {
  return cmds.map((c) => (c.cmd === "Z" ? "Z" : `${c.cmd}${c.nums.join(" ")}`)).join(" ");
}

/** Aggiorna un punto specifico (cmdIdx + numIdx) e restituisce il nuovo path. */
function updatePathPoint(d, cmdIdx, numIdx, newX, newY) {
  const cmds = parsePathCmds(d);
  if (!cmds[cmdIdx]) return d;
  const nums = [...cmds[cmdIdx].nums];
  nums[numIdx] = newX;
  nums[numIdx + 1] = newY;
  cmds[cmdIdx] = { ...cmds[cmdIdx], nums };
  return serializePathCmds(cmds);
}

/** Bounding box di un path SVG semplice (solo M/L/C/Q). */
function pathBbox(d) {
  if (!d) return null;
  const nums = [];
  d.replace(/([MLCQ])\s*([^MLCQZ]+)/gi, (_, cmd, rest) => {
    rest.trim().split(/[\s,]+/).filter((s) => s !== "").forEach((s) => nums.push(Number(s)));
    return "";
  });
  if (nums.length < 2) return null;
  const xs = [], ys = [];
  for (let i = 0; i < nums.length - 1; i += 2) { xs.push(nums[i]); ys.push(nums[i + 1]); }
  return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
}

function bboxIntersect(a, b) {
  if (!a || !b) return false;
  return !(a.x1 < b.x0 || a.x0 > b.x1 || a.y1 < b.y0 || a.y0 > b.y1);
}

function bboxContains(bbox, pt) {
  return bbox && pt.x >= bbox.x0 && pt.x <= bbox.x1 && pt.y >= bbox.y0 && pt.y <= bbox.y1;
}

// ----- component -------------------------------------------------------------

export default function SurfaceEditor() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const imgRef = useRef(null);

  const [product, setProduct] = useState(null);
  const [bgImage, setBgImage] = useState(null);
  const [bgConfig, setBgConfig] = useState({ scale: 1.0, offset_x: 0, offset_y: 0, opacity: 1.0, visible: true, blend_mode: "normal", lock_canvas: false });
  const [canvasConfig, setCanvasConfig] = useState({ width: 1200, height: 1200, aspect: "square" });
  const [layers, setLayers] = useState([]);
  const [groupsConfig] = useState(() =>
    SURFACE_GROUPS.reduce((acc, g, i) => ({ ...acc, [g.id]: { label: g.label, picker_axes: g.picker_axes, order: i + 1 } }), {})
  );

  const [tool, setTool] = useState("pen");
  const [tolerance, setTolerance] = useState(20);
  const [zoom, setZoom] = useState(0.6);

  // Multi-selection: set of layer ids
  const [selectedIds, setSelectedIds] = useState(new Set());
  const activeId = selectedIds.size === 1 ? [...selectedIds][0] : null;

  // Drawing states
  const [drawingPoints, setDrawingPoints] = useState([]);
  const [hoverPoint, setHoverPoint] = useState(null);
  const [edgeMap, setEdgeMap] = useState(null);

  // Bezier
  const [bezierNodes, setBezierNodes] = useState([]);
  const [bezierDrag, setBezierDrag] = useState(null);

  // Move tool: marquee + drag
  const [marquee, setMarquee] = useState(null); // {x0,y0,x1,y1}
  const [dragMove, setDragMove] = useState(null); // {startX, startY, originalPaths: {[id]: original_d}}

  // Vertex editing (modifica singoli punti del path)
  const [vertexDrag, setVertexDrag] = useState(null); // {layerId, cmdIdx, numIdx, originalPath}
  const [hoverVertex, setHoverVertex] = useState(null); // {layerId, cmdIdx, numIdx}

  // Clipboard
  const [clipboard, setClipboard] = useState([]); // array of layer copies
  const [pasteCount, setPasteCount] = useState(0); // for cascade offset

  // Image paste prompt
  const [pasteImagePrompt, setPasteImagePrompt] = useState(null); // {dataUrl}

  // Inline rename
  const [renameId, setRenameId] = useState(null);

  // Misc
  const [onionSkin, setOnionSkin] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showShadingModal, setShowShadingModal] = useState(false);

  // History
  const [historyPast, setHistoryPast] = useState([]);
  const [historyFuture, setHistoryFuture] = useState([]);

  const recordHistory = useCallback(() => {
    setHistoryPast((p) => {
      const snap = {
        layers: JSON.parse(JSON.stringify(layers)),
        bezierNodes: JSON.parse(JSON.stringify(bezierNodes)),
        drawingPoints: JSON.parse(JSON.stringify(drawingPoints)),
      };
      return [...p.slice(-(HISTORY_MAX - 1)), snap];
    });
    setHistoryFuture([]);
  }, [layers, bezierNodes, drawingPoints]);

  const undo = useCallback(() => {
    setHistoryPast((past) => {
      if (past.length === 0) { toast.info("Niente da annullare"); return past; }
      const last = past[past.length - 1];
      setHistoryFuture((f) => [{
        layers: JSON.parse(JSON.stringify(layers)),
        bezierNodes: JSON.parse(JSON.stringify(bezierNodes)),
        drawingPoints: JSON.parse(JSON.stringify(drawingPoints)),
      }, ...f].slice(0, HISTORY_MAX));
      setLayers(last.layers || []);
      setBezierNodes(last.bezierNodes || []);
      setDrawingPoints(last.drawingPoints || []);
      return past.slice(0, -1);
    });
  }, [layers, bezierNodes, drawingPoints]);

  const redo = useCallback(() => {
    setHistoryFuture((future) => {
      if (future.length === 0) { toast.info("Niente da ripristinare"); return future; }
      const next = future[0];
      setHistoryPast((p) => [...p.slice(-(HISTORY_MAX - 1)), {
        layers: JSON.parse(JSON.stringify(layers)),
        bezierNodes: JSON.parse(JSON.stringify(bezierNodes)),
        drawingPoints: JSON.parse(JSON.stringify(drawingPoints)),
      }]);
      setLayers(next.layers || []);
      setBezierNodes(next.bezierNodes || []);
      setDrawingPoints(next.drawingPoints || []);
      return future.slice(1);
    });
  }, [layers, bezierNodes, drawingPoints]);

  // ----- Load product
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/products/${productId}/surfaces`);
        setProduct(r.data);
        if (r.data.canvas_config) setCanvasConfig(r.data.canvas_config);
        if (r.data.background_config) setBgConfig({ scale: 1, offset_x: 0, offset_y: 0, opacity: 1, visible: true, ...r.data.background_config });
        if (r.data.layers?.length) {
          setLayers(r.data.layers);
          setSelectedIds(new Set([r.data.layers[0].id]));
        }
        if (r.data.background_image_url) {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => { setBgImage(img); imgRef.current = img; };
          const url = r.data.background_image_url;
          img.src = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
        }
      } catch (e) {
        toast.error("Errore caricamento: " + (e.response?.data?.detail || e.message));
      } finally { setLoading(false); }
    })();
  }, [productId]);

  // ----- Edge map (Sobel) per penna magnetica
  const computeEdgeMap = useCallback((imgEl) => {
    const off = document.createElement("canvas");
    off.width = imgEl.naturalWidth;
    off.height = imgEl.naturalHeight;
    const ctx = off.getContext("2d");
    ctx.drawImage(imgEl, 0, 0);
    const src = ctx.getImageData(0, 0, off.width, off.height);
    const out = new Uint8ClampedArray(off.width * off.height);
    const W = off.width;
    const gray = new Float32Array(W * off.height);
    for (let i = 0; i < gray.length; i++) {
      gray[i] = 0.299 * src.data[i * 4] + 0.587 * src.data[i * 4 + 1] + 0.114 * src.data[i * 4 + 2];
    }
    for (let y = 1; y < off.height - 1; y++) {
      for (let x = 1; x < W - 1; x++) {
        const i = y * W + x;
        const gx = -gray[i - W - 1] - 2 * gray[i - 1] - gray[i + W - 1] + gray[i - W + 1] + 2 * gray[i + 1] + gray[i + W + 1];
        const gy = -gray[i - W - 1] - 2 * gray[i - W] - gray[i - W + 1] + gray[i + W - 1] + 2 * gray[i + W] + gray[i + W + 1];
        out[i] = Math.min(255, Math.hypot(gx, gy));
      }
    }
    return { data: out, width: W, height: off.height };
  }, []);

  // URL della nuova foto sul server (impostato dopo upload reale, viene incluso nel save blueprint)
  const [pendingBgUrl, setPendingBgUrl] = useState(null);

  const onUploadBg = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // 1) Upload reale al server (per persistenza nel B2C)
    let uploadedUrl = null;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      uploadedUrl = r.data.url;
      setPendingBgUrl(uploadedUrl);
    } catch (err) {
      toast.error("Errore upload foto: " + (err.response?.data?.detail || err.message));
      return;
    }
    // 2) AUTO-SAVE immediato del background sul prodotto: così la foto è subito
    //    persistente per AI, riapertura editor e vetrina B2C — l'utente non deve
    //    cliccare "Salva blueprint" solo per applicare la foto.
    try {
      await api.put(`/products/${productId}`, { immagini: [uploadedUrl] });
      setProduct((p) => p ? { ...p, immagini: [uploadedUrl], background_image_url: uploadedUrl, updated_at: new Date().toISOString() } : p);
      setPendingBgUrl(null);
      toast.success("Foto sfondo salvata sul prodotto (visibile in B2C e all'AI)");
    } catch (err) {
      toast.error("Foto caricata ma non collegata al prodotto: " + (err.response?.data?.detail || err.message));
    }
    // 3) Preview locale (dimensioni naturali per setare il canvas se non locked)
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        setBgImage(img);
        if (!bgConfig.lock_canvas) {
          setCanvasConfig({ width: img.naturalWidth, height: img.naturalHeight, aspect: img.naturalWidth === img.naturalHeight ? "square" : (img.naturalWidth > img.naturalHeight ? "landscape" : "portrait") });
        }
        setBgConfig((b) => ({ ...b, visible: true, opacity: b.opacity > 0 ? b.opacity : 1 }));
        setEdgeMap(computeEdgeMap(img));
      };
      img.src = ev.target.result;
      imgRef.current = img;
    };
    reader.readAsDataURL(file);
  };

  const clearBackground = () => {
    if (!window.confirm("Rimuovere lo sfondo dalla scena? La foto base non sarà più visibile né nel Surface Editor né nel B2C. Potrai ricaricarla in qualunque momento.")) return;
    setBgConfig((b) => ({ ...b, visible: false, opacity: 0 }));
    toast.success("Sfondo rimosso dalla scena");
  };

  const toCanvasCoords = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: Math.round((e.clientX - rect.left) / zoom),
      y: Math.round((e.clientY - rect.top) / zoom),
    };
  };

  const snapToEdge = (pt) => {
    if (!edgeMap || tool !== "pen") return pt;
    const R = 12;
    let best = pt, maxEdge = 0;
    for (let dy = -R; dy <= R; dy++) {
      for (let dx = -R; dx <= R; dx++) {
        const x = pt.x + dx, y = pt.y + dy;
        if (x < 0 || y < 0 || x >= edgeMap.width || y >= edgeMap.height) continue;
        const v = edgeMap.data[y * edgeMap.width + x];
        if (v > maxEdge && v > 60) { maxEdge = v; best = { x, y }; }
      }
    }
    return best;
  };

  // ----- BEZIER
  const onBezierMouseDown = (e) => {
    e.preventDefault();
    const raw = toCanvasCoords(e);
    if (bezierNodes.length >= 2) {
      const first = bezierNodes[0];
      const dist = Math.hypot(first.x - raw.x, first.y - raw.y);
      if (dist < 18 / zoom) {
        recordHistory();
        commitBezierLayer(bezierNodesToPath(bezierNodes, true));
        return;
      }
    }
    recordHistory();
    setBezierNodes((nodes) => [...nodes, { x: raw.x, y: raw.y, hIn: null, hOut: null, mirror: true }]);
    setBezierDrag({ nodeIdx: bezierNodes.length, altKey: e.altKey });
  };

  const onBezierMouseMove = (e) => {
    const raw = toCanvasCoords(e);
    setHoverPoint(raw);
    if (!bezierDrag) return;
    const { nodeIdx, altKey } = bezierDrag;
    setBezierNodes((nodes) => {
      const arr = [...nodes];
      const n = { ...arr[nodeIdx] };
      n.hOut = { x: raw.x, y: raw.y };
      if (!altKey) {
        n.hIn = { x: 2 * n.x - raw.x, y: 2 * n.y - raw.y };
        n.mirror = true;
      } else n.mirror = false;
      arr[nodeIdx] = n;
      return arr;
    });
  };

  const onBezierMouseUp = () => {
    if (!bezierDrag) return;
    setBezierNodes((nodes) => {
      const arr = [...nodes];
      const n = arr[bezierDrag.nodeIdx];
      if (n?.hOut && Math.hypot(n.hOut.x - n.x, n.hOut.y - n.y) < 4) {
        arr[bezierDrag.nodeIdx] = { ...n, hIn: null, hOut: null };
      }
      return arr;
    });
    setBezierDrag(null);
  };

  const commitBezierLayer = (pathOverride) => {
    if (bezierNodes.length < 2) { setBezierNodes([]); setBezierDrag(null); return; }
    const closed = !!pathOverride;
    const path = pathOverride || bezierNodesToPath(bezierNodes, false);
    addLayer({ svg_path: path, bezier_nodes: bezierNodes, closed });
    setBezierNodes([]); setBezierDrag(null);
    toast.success(`Path Bezier creato (${bezierNodes.length} ancore)`);
  };

  // ----- PEN + WAND (click-based)
  const onPenClick = (e) => {
    const raw = toCanvasCoords(e);
    if (tool === "pen") {
      recordHistory();
      setDrawingPoints((pts) => [...pts, snapToEdge(raw)]);
    } else if (tool === "wand") {
      magicWand(raw);
    }
  };

  const magicWand = (start) => {
    if (!imgRef.current) return toast.error("Carica prima la foto base");
    const off = document.createElement("canvas");
    off.width = imgRef.current.naturalWidth;
    off.height = imgRef.current.naturalHeight;
    const ctx = off.getContext("2d");
    ctx.drawImage(imgRef.current, 0, 0);
    const data = ctx.getImageData(0, 0, off.width, off.height);
    const W = off.width, H = off.height;
    const idx = (start.y * W + start.x) * 4;
    const tR = data.data[idx], tG = data.data[idx + 1], tB = data.data[idx + 2];
    const tol = tolerance * 2.55;
    const visited = new Uint8Array(W * H);
    const stack = [[start.x, start.y]];
    const points = [];
    while (stack.length) {
      const [x, y] = stack.pop();
      if (x < 0 || y < 0 || x >= W || y >= H) continue;
      const p = y * W + x;
      if (visited[p]) continue;
      visited[p] = 1;
      const i = p * 4;
      if (Math.abs(data.data[i] - tR) < tol && Math.abs(data.data[i + 1] - tG) < tol && Math.abs(data.data[i + 2] - tB) < tol) {
        points.push([x, y]);
        stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
      }
    }
    if (points.length < 50) return toast.error("Selezione troppo piccola");
    const hull = boundaryHull(points);
    recordHistory();
    addLayerFromPoints(hull);
    toast.success(`Selezionati ${points.length} pixel`);
  };

  const boundaryHull = (points) => {
    const byY = {};
    points.forEach(([x, y]) => {
      if (!byY[y]) byY[y] = [x, x];
      else { byY[y] = [Math.min(byY[y][0], x), Math.max(byY[y][1], x)]; }
    });
    const ys = Object.keys(byY).map(Number).sort((a, b) => a - b);
    const left = [], right = [];
    ys.forEach((y) => { left.push({ x: byY[y][0], y }); right.push({ x: byY[y][1], y }); });
    return [...left, ...right.reverse()];
  };

  const pointsToSvgPath = (pts) => {
    if (pts.length < 3) return "";
    let d = `M${pts[0].x} ${pts[0].y}`;
    for (let i = 1; i < pts.length - 1; i++) {
      const mid = { x: (pts[i].x + pts[i + 1].x) / 2, y: (pts[i].y + pts[i + 1].y) / 2 };
      d += ` Q${pts[i].x} ${pts[i].y} ${mid.x} ${mid.y}`;
    }
    d += ` L${pts[pts.length - 1].x} ${pts[pts.length - 1].y} Z`;
    return d;
  };

  const addLayer = (partial) => {
    const newLayer = {
      id: crypto.randomUUID(),
      name: `Layer ${layers.length + 1}`,
      svg_path: "",
      anchor: { x: 0, y: 0, scale: 1, rotation: 0 },
      z_index: layers.length + 1,
      surface_group: "upholstery",
      visible: true,
      color: LAYER_COLORS[layers.length % LAYER_COLORS.length],
      group_id: null,
      ...partial,
    };
    setLayers((l) => [...l, newLayer]);
    setSelectedIds(new Set([newLayer.id]));
    return newLayer;
  };

  const addLayerFromPoints = (pts) => {
    if (pts.length < 3) return;
    addLayer({ svg_path: pointsToSvgPath(pts), points: pts });
    setDrawingPoints([]);
  };

  const finishPolygon = useCallback(() => {
    if (tool === "bezier" && bezierNodes.length >= 2) {
      recordHistory();
      commitBezierLayer();
      return;
    }
    if (drawingPoints.length >= 3) {
      recordHistory();
      addLayerFromPoints(drawingPoints);
    }
    setDrawingPoints([]);
  }, [drawingPoints, bezierNodes, tool, recordHistory]);

  // ----- MOVE TOOL (drag + marquee + multi-select) -----------------------

  const findLayerAt = (pt) => {
    // Topmost (highest z_index) layer whose bbox contains pt
    const sorted = [...layers].sort((a, b) => (b.z_index || 0) - (a.z_index || 0));
    for (const l of sorted) {
      if (l.visible === false) continue;
      const bb = pathBbox(l.svg_path);
      if (bboxContains(bb, pt)) return l;
    }
    return null;
  };

  /** Trova il vertice (anchor o ctrl) sotto al puntatore se il layer è selezionato. */
  const findVertexAt = (pt) => {
    if (selectedIds.size !== 1) return null;
    const layerId = [...selectedIds][0];
    const layer = layers.find((l) => l.id === layerId);
    if (!layer || layer.image_layer) return null;
    const { points } = pathEditablePoints(layer.svg_path);
    const R = 10 / zoom; // hit radius in canvas units
    for (const p of points) {
      if (Math.hypot(p.x - pt.x, p.y - pt.y) < R) return { ...p, layerId };
    }
    return null;
  };

  const onMoveMouseDown = (e) => {
    const pt = toCanvasCoords(e);
    // First check: hit a vertex of the selected layer?
    const vh = findVertexAt(pt);
    if (vh) {
      const layer = layers.find((l) => l.id === vh.layerId);
      recordHistory();
      setVertexDrag({ layerId: vh.layerId, cmdIdx: vh.cmdIdx, numIdx: vh.numIdx, originalPath: layer.svg_path });
      return;
    }
    const hit = findLayerAt(pt);
    if (hit) {
      let nextSel;
      if (e.shiftKey) {
        nextSel = new Set(selectedIds);
        nextSel.has(hit.id) ? nextSel.delete(hit.id) : nextSel.add(hit.id);
      } else if (selectedIds.has(hit.id)) {
        nextSel = new Set(selectedIds);
      } else {
        nextSel = new Set([hit.id]);
      }
      if (!e.shiftKey && hit.group_id) {
        layers.forEach((l) => { if (l.group_id === hit.group_id) nextSel.add(l.id); });
      }
      setSelectedIds(nextSel);
      recordHistory();
      const originalPaths = {};
      layers.forEach((l) => { if (nextSel.has(l.id)) originalPaths[l.id] = l.svg_path; });
      setDragMove({ startX: pt.x, startY: pt.y, originalPaths });
    } else {
      if (!e.shiftKey) setSelectedIds(new Set());
      setMarquee({ x0: pt.x, y0: pt.y, x1: pt.x, y1: pt.y });
    }
  };

  const onMoveMouseMove = (e) => {
    const pt = toCanvasCoords(e);
    if (vertexDrag) {
      setLayers((ls) => ls.map((l) => {
        if (l.id !== vertexDrag.layerId) return l;
        return { ...l, svg_path: updatePathPoint(vertexDrag.originalPath, vertexDrag.cmdIdx, vertexDrag.numIdx, pt.x, pt.y) };
      }));
      return;
    }
    if (dragMove) {
      const dx = pt.x - dragMove.startX;
      const dy = pt.y - dragMove.startY;
      setLayers((ls) => ls.map((l) => {
        if (!selectedIds.has(l.id)) return l;
        const orig = dragMove.originalPaths[l.id];
        return { ...l, svg_path: shiftSvgPath(orig, dx, dy) };
      }));
    } else if (marquee) {
      setMarquee({ ...marquee, x1: pt.x, y1: pt.y });
    } else {
      // Hover detection per cambiare cursore sui vertici
      setHoverVertex(findVertexAt(pt));
    }
  };

  const onMoveMouseUp = () => {
    if (vertexDrag) { setVertexDrag(null); return; }
    if (marquee) {
      const box = {
        x0: Math.min(marquee.x0, marquee.x1),
        y0: Math.min(marquee.y0, marquee.y1),
        x1: Math.max(marquee.x0, marquee.x1),
        y1: Math.max(marquee.y0, marquee.y1),
      };
      if (Math.abs(box.x1 - box.x0) > 3 && Math.abs(box.y1 - box.y0) > 3) {
        const hit = layers.filter((l) => l.visible !== false && bboxIntersect(pathBbox(l.svg_path), box));
        setSelectedIds(new Set(hit.map((l) => l.id)));
        if (hit.length > 0) toast.success(`${hit.length} layer selezionato/i`);
      }
      setMarquee(null);
    }
    setDragMove(null);
  };

  // ----- UNIFIED canvas event dispatchers --------------------------------
  const onCanvasClick = (e) => {
    if (tool === "pen" || tool === "wand") onPenClick(e);
  };
  const onCanvasMouseDown = (e) => {
    if (tool === "bezier") onBezierMouseDown(e);
    else if (tool === "move") onMoveMouseDown(e);
  };
  const onCanvasMouseMove = (e) => {
    const raw = toCanvasCoords(e);
    if (tool === "pen") setHoverPoint(snapToEdge(raw));
    else if (tool === "bezier") onBezierMouseMove(e);
    else if (tool === "move") onMoveMouseMove(e);
  };
  const onCanvasMouseUp = () => {
    if (tool === "bezier") onBezierMouseUp();
    else if (tool === "move") onMoveMouseUp();
  };

  // ----- GROUP / UNGROUP / COPY / PASTE ----------------------------------

  const groupSelected = () => {
    if (selectedIds.size < 2) return toast.info("Seleziona almeno 2 layer per raggruppare");
    recordHistory();
    const gid = crypto.randomUUID();
    setLayers((ls) => ls.map((l) => selectedIds.has(l.id) ? { ...l, group_id: gid } : l));
    toast.success(`Gruppo creato (${selectedIds.size} layer)`);
  };

  const ungroupSelected = () => {
    const ids = layers.filter((l) => selectedIds.has(l.id) && l.group_id).map((l) => l.id);
    if (!ids.length) return toast.info("Nessun gruppo da sciogliere");
    recordHistory();
    setLayers((ls) => ls.map((l) => ids.includes(l.id) ? { ...l, group_id: null } : l));
    toast.success("Gruppo sciolto");
  };

  const copySelected = () => {
    if (selectedIds.size === 0) return;
    const sel = layers.filter((l) => selectedIds.has(l.id));
    setClipboard(JSON.parse(JSON.stringify(sel)));
    setPasteCount(0);
    toast.success(`Copiato ${sel.length} layer`);
  };

  const pasteClipboard = () => {
    if (clipboard.length === 0) return toast.info("Clipboard vuoto");
    recordHistory();
    const offset = 30 * (pasteCount + 1);
    const oldToNewGid = {};
    const newLayers = clipboard.map((src, i) => {
      let gid = src.group_id;
      if (gid) {
        if (!oldToNewGid[gid]) oldToNewGid[gid] = crypto.randomUUID();
        gid = oldToNewGid[gid];
      }
      return {
        ...src,
        id: crypto.randomUUID(),
        name: src.name + " (copia)",
        svg_path: shiftSvgPath(src.svg_path, offset, offset),
        group_id: gid,
        z_index: (layers.length + i + 1),
        color: LAYER_COLORS[(layers.length + i) % LAYER_COLORS.length],
      };
    });
    setLayers((l) => [...l, ...newLayers]);
    setSelectedIds(new Set(newLayers.map((l) => l.id)));
    setPasteCount((c) => c + 1);
    toast.success(`Incollato ${newLayers.length} layer`);
  };

  const deleteSelected = () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Eliminare ${selectedIds.size} layer?`)) return;
    recordHistory();
    setLayers((ls) => ls.filter((l) => !selectedIds.has(l.id)));
    setSelectedIds(new Set());
  };

  // ----- PASTE IMAGE FROM CLIPBOARD --------------------------------------

  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const it of items) {
        if (it.type.startsWith("image/")) {
          const blob = it.getAsFile();
          const reader = new FileReader();
          reader.onload = (ev) => setPasteImagePrompt({ dataUrl: ev.target.result });
          reader.readAsDataURL(blob);
          e.preventDefault();
          return;
        }
      }
    };
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, []);

  const acceptPasteAsBackground = () => {
    if (!pasteImagePrompt) return;
    const img = new Image();
    img.onload = () => {
      setBgImage(img);
      setCanvasConfig({ width: img.naturalWidth, height: img.naturalHeight, aspect: img.naturalWidth === img.naturalHeight ? "square" : (img.naturalWidth > img.naturalHeight ? "landscape" : "portrait") });
      setEdgeMap(computeEdgeMap(img));
      imgRef.current = img;
      toast.success("Foto base aggiornata");
    };
    img.src = pasteImagePrompt.dataUrl;
    setPasteImagePrompt(null);
  };

  const acceptPasteAsLayer = () => {
    if (!pasteImagePrompt) return;
    recordHistory();
    const img = new Image();
    img.onload = () => {
      const w = Math.min(img.naturalWidth, canvasConfig.width * 0.5);
      const h = w * (img.naturalHeight / img.naturalWidth);
      const x = canvasConfig.width / 2 - w / 2;
      const y = canvasConfig.height / 2 - h / 2;
      addLayer({
        name: "Immagine incollata",
        image_layer: true,
        image_url: pasteImagePrompt.dataUrl,
        image_x: x, image_y: y, image_w: w, image_h: h,
        svg_path: `M${x} ${y} L${x + w} ${y} L${x + w} ${y + h} L${x} ${y + h} Z`,
        surface_group: "image",
      });
      toast.success("Immagine inserita come layer");
    };
    img.src = pasteImagePrompt.dataUrl;
    setPasteImagePrompt(null);
  };

  // ----- KEYBOARD --------------------------------------------------------
  useEffect(() => {
    const handler = (e) => {
      const isMod = e.metaKey || e.ctrlKey;
      // Avoid hijacking inputs
      const tag = (e.target?.tagName || "").toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA") return;

      if (isMod && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo(); return;
      }
      if (isMod && (e.key === "y" || e.key === "Y")) { e.preventDefault(); redo(); return; }
      if (isMod && (e.key === "g" || e.key === "G")) {
        e.preventDefault();
        if (e.shiftKey) ungroupSelected(); else groupSelected();
        return;
      }
      if (isMod && (e.key === "c" || e.key === "C")) { e.preventDefault(); copySelected(); return; }
      if (isMod && (e.key === "v" || e.key === "V")) {
        // If clipboard image will be handled by document paste event; only handle layer paste here
        if (clipboard.length > 0) { e.preventDefault(); pasteClipboard(); return; }
      }
      if (isMod && (e.key === "a" || e.key === "A")) {
        e.preventDefault();
        setSelectedIds(new Set(layers.map((l) => l.id))); return;
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        if (tool === "bezier" && bezierNodes.length > 0) { setBezierNodes((n) => n.slice(0, -1)); return; }
        if (drawingPoints.length > 0) { setDrawingPoints((pts) => pts.slice(0, -1)); return; }
        if (selectedIds.size > 0 && e.key === "Delete") { deleteSelected(); return; }
      }
      if (e.key === "Enter") finishPolygon();
      if (e.key === "Escape") {
        setDrawingPoints([]); setBezierNodes([]); setBezierDrag(null);
        setSelectedIds(new Set()); setMarquee(null); setPasteImagePrompt(null);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [drawingPoints, bezierNodes, tool, undo, redo, finishPolygon, selectedIds, clipboard, layers]);

  // ----- LAYER OPS -------------------------------------------------------

  const updateLayer = (id, updates) => {
    setLayers((ls) => ls.map((l) => (l.id === id ? { ...l, ...updates } : l)));
  };

  // When a layer's surface_group is changed and it belongs to a group → propagate
  const updateLayerSurfaceGroup = (id, newGroup) => {
    const target = layers.find((l) => l.id === id);
    if (!target) return;
    recordHistory();
    setLayers((ls) => ls.map((l) => {
      if (l.id === id) return { ...l, surface_group: newGroup };
      if (target.group_id && l.group_id === target.group_id) return { ...l, surface_group: newGroup };
      return l;
    }));
    if (target.group_id) toast.success("Surface group applicato al gruppo");
  };

  const removeLayer = (id) => {
    if (!window.confirm("Eliminare questo layer?")) return;
    recordHistory();
    setLayers((ls) => ls.filter((l) => l.id !== id));
    setSelectedIds((s) => new Set([...s].filter((x) => x !== id)));
  };

  // Z-order: porta avanti/indietro/in primo piano/in fondo
  const reorderLayer = (id, direction) => {
    setLayers((ls) => {
      const sorted = [...ls].sort((a, b) => (a.z_index || 0) - (b.z_index || 0));
      const i = sorted.findIndex((x) => x.id === id);
      if (i < 0) return ls;
      const maxZ = sorted[sorted.length - 1]?.z_index || 0;
      const minZ = sorted[0]?.z_index || 0;
      let newZ = sorted[i].z_index || 0;
      if (direction === "forward" && i < sorted.length - 1) newZ = (sorted[i + 1].z_index || 0) + 0.5;
      else if (direction === "backward" && i > 0) newZ = (sorted[i - 1].z_index || 0) - 0.5;
      else if (direction === "front") newZ = maxZ + 1;
      else if (direction === "back") newZ = minZ - 1;
      return ls.map((l) => (l.id === id ? { ...l, z_index: newZ } : l));
    });
    // Normalizza i z_index a interi consecutivi
    setTimeout(() => {
      setLayers((ls) => {
        const sorted = [...ls].sort((a, b) => (a.z_index || 0) - (b.z_index || 0));
        return ls.map((l) => {
          const newZ = sorted.findIndex((x) => x.id === l.id) + 1;
          return { ...l, z_index: newZ };
        });
      });
    }, 0);
  };

  // Sposta il layer selezionato con le frecce della tastiera (1px, 10px con Shift)
  useEffect(() => {
    const onKey = (e) => {
      const sel = [...selectedIds];
      if (sel.length !== 1) return;
      if (e.target?.tagName === "INPUT" || e.target?.tagName === "TEXTAREA" || e.target?.tagName === "SELECT") return;
      const arrowMap = { ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0] };
      const delta = arrowMap[e.key];
      if (!delta) return;
      e.preventDefault();
      const step = e.shiftKey ? 10 : 1;
      const dx = delta[0] * step;
      const dy = delta[1] * step;
      const id = sel[0];
      setLayers((ls) => ls.map((l) => {
        if (l.id !== id) return l;
        // Per layer maschera: trasla il path SVG
        if (!l.svg_path) return l;
        const moved = l.svg_path.replace(/(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)/g, (_, x, y) => `${(parseFloat(x) + dx).toFixed(2)} ${(parseFloat(y) + dy).toFixed(2)}`);
        return { ...l, svg_path: moved };
      }));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedIds]);

  const loadQuickTemplate = (template) => {
    if (layers.length > 0 && !window.confirm(`Sostituire i ${layers.length} layer correnti con il template "${template.label}"?`)) return;
    recordHistory();
    const newLayers = template.layers.map((l, i) => ({
      ...l,
      id: crypto.randomUUID(),
      anchor: { x: 0, y: 0, scale: 1, rotation: 0 },
      visible: true,
      color: LAYER_COLORS[i % LAYER_COLORS.length],
      group_id: null,
    }));
    setLayers(newLayers);
    setSelectedIds(new Set(newLayers[0] ? [newLayers[0].id] : []));
    setShowTemplates(false);
    toast.success(`Template "${template.label}" caricato (${newLayers.length} layer)`);
  };

  const saveBlueprint = async () => {
    setSaving(true);
    try {
      const payload = {
        canvas_config: canvasConfig,
        layers: layers.map((l) => {
          const { color: _c, visible: _v, ...rest } = l;
          return rest;
        }),
        surface_groups_config: groupsConfig,
        background_config: bgConfig,
      };
      // Se l'utente ha caricato una nuova foto, aggiornala sul prodotto
      if (pendingBgUrl) {
        payload.background_image_url = pendingBgUrl;
      }
      await api.put(`/products/${productId}/surfaces`, payload);
      if (pendingBgUrl) {
        setPendingBgUrl(null);
        toast.success(`Blueprint + nuova foto salvati: ${layers.length} layer applicati al B2C`);
      } else {
        toast.success(`Blueprint salvato: ${layers.length} layer`);
      }
    } catch (e) {
      toast.error("Errore salvataggio: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  // Fase 2: apre il modal con anteprima live + curves editor
  const generateShadingMap = () => setShowShadingModal(true);

  /** URL della foto attualmente visibile nell'editor (priorità: nuovo upload non
   *  ancora salvato → foto base del prodotto). Lo shading map verrà generato da
   *  questa, NON dal vecchio `product.immagini[0]` cached. */
  const currentBgUrl = pendingBgUrl || product?.background_image_url || null;

  /** Crea un layer shading map a partire dal risultato del modal. Ogni
   *  rigenerazione crea un layer nuovo, eliminabile come gli altri. */
  const handleCreateShadingLayer = ({ shading_url, params }) => {
    if (!shading_url) return;
    recordHistory();
    const maxZ = layers.reduce((m, l) => Math.max(m, l.z_index || 0), 0);
    const idx = layers.filter((l) => l.layer_type === "shading").length + 1;
    const newLayer = {
      id: crypto.randomUUID(),
      name: `Shading map ${idx}`,
      svg_path: "",
      anchor: { x: 0, y: 0, scale: 1, rotation: 0 },
      z_index: maxZ + 1,
      surface_group: "upholstery",
      visible: true,
      color: LAYER_COLORS[layers.length % LAYER_COLORS.length],
      group_id: null,
      layer_type: "shading",
      image_layer: true,
      image_url: shading_url,
      image_x: 0,
      image_y: 0,
      image_w: canvasConfig.width,
      image_h: canvasConfig.height,
      blend_mode: "multiply",
      opacity: 0.7,
      shading_params: params,
    };
    setLayers((l) => [...l, newLayer]);
    setSelectedIds(new Set([newLayer.id]));
  };

  // 🤖 Rilevamento AI dei layer dalla foto base
  const aiDetectLayers = async () => {
    if (layers.length > 0 && !window.confirm(`Sostituire i ${layers.length} layer esistenti con quelli rilevati dall'AI?`)) return;
    setSaving(true);
    toast.info("L'AI sta analizzando la foto…", { duration: 8000 });
    try {
      const src = currentBgUrl || pendingBgUrl;
      const qs = src ? `?source_url=${encodeURIComponent(src)}` : "";
      const r = await api.post(`/products/${productId}/ai-detect-layers${qs}`);
      const proposed = r.data?.proposed_layers || [];
      if (!proposed.length) {
        toast.error("L'AI non ha riconosciuto layer in questa foto. Disegnali a mano.");
        return;
      }
      recordHistory();
      const newLayers = proposed.map((l, i) => ({
        id: crypto.randomUUID(),
        name: l.name,
        svg_path: l.svg_path,
        anchor: { x: 0, y: 0, scale: 1, rotation: 0 },
        z_index: l.z_index || i + 1,
        surface_group: l.surface_group || "upholstery",
        visible: true,
        color: LAYER_COLORS[i % LAYER_COLORS.length],
        group_id: null,
        ai_generated: true,
      }));
      setLayers(newLayers);
      setSelectedIds(new Set(newLayers[0] ? [newLayers[0].id] : []));
      toast.success(`AI ha rilevato ${newLayers.length} layer — modificali liberamente!`);
    } catch (e) {
      toast.error("Errore AI: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  if (loading) return <AdminLayout><div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="min-h-screen flex flex-col" data-testid="surface-editor">
        {/* Header */}
        <div className="border-b border-border bg-white px-6 py-3 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5" data-testid="se-back">
            <ChevronLeft className="w-4 h-4" /> Indietro
          </button>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Compositore visuale</div>
            <h1 className="font-bold text-base">{product?.nome} <span className="text-xs font-mono text-muted-foreground">{product?.sku_madre}</span></h1>
          </div>

          {/* Bulk actions */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-sm bg-brand/10 border border-brand/30" data-testid="se-bulk-bar">
              <span className="text-xs font-medium text-brand mr-1">{selectedIds.size} selezionati</span>
              <button onClick={groupSelected} title="Raggruppa (Ctrl+G)" className="p-1.5 hover:bg-white rounded-sm" data-testid="se-group"><Group className="w-3.5 h-3.5" /></button>
              <button onClick={ungroupSelected} title="Sgruppa (Ctrl+Shift+G)" className="p-1.5 hover:bg-white rounded-sm" data-testid="se-ungroup"><Ungroup className="w-3.5 h-3.5" /></button>
              <button onClick={copySelected} title="Copia (Ctrl+C)" className="p-1.5 hover:bg-white rounded-sm" data-testid="se-copy"><Copy className="w-3.5 h-3.5" /></button>
              <button onClick={pasteClipboard} title="Incolla (Ctrl+V)" className="p-1.5 hover:bg-white rounded-sm" data-testid="se-paste"><ClipboardPaste className="w-3.5 h-3.5" /></button>
              <button onClick={deleteSelected} title="Elimina (Del)" className="p-1.5 hover:bg-white rounded-sm text-red-600" data-testid="se-delete-selected"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          )}

          <div className="relative">
            <button onClick={() => setShowTemplates((v) => !v)} className="h-9 px-3 rounded-sm border border-border hover:bg-accent text-xs inline-flex items-center gap-1.5" data-testid="se-templates-btn">
              <Sparkles className="w-3.5 h-3.5" /> Template rapidi
            </button>
            {showTemplates && (
              <div className="absolute right-0 mt-1 w-56 bg-white border border-border rounded-sm shadow-lg z-30" data-testid="se-templates-menu">
                <div className="px-3 py-2 border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">Quick start</div>
                {QUICK_TEMPLATES.map((t) => (
                  <button key={t.id} onClick={() => loadQuickTemplate(t)} data-testid={`se-tpl-${t.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-accent text-sm flex items-center gap-2 border-b border-border last:border-0">
                    <span className="text-lg leading-none">{t.emoji}</span>
                    <div className="flex-1">
                      <div className="font-medium">{t.label}</div>
                      <div className="text-[10px] text-muted-foreground">{t.layers.length} layer</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <label className="h-9 px-3 rounded-sm border border-border hover:bg-accent text-xs inline-flex items-center gap-1.5 cursor-pointer" data-testid="se-upload-bg">
            <Upload className="w-3.5 h-3.5" /> Carica foto scocca
            <input type="file" accept="image/*" onChange={onUploadBg} className="hidden" />
          </label>
          {pendingBgUrl && (
            <div className="px-2 py-1 rounded-sm bg-amber-100 border border-amber-300 text-amber-900 text-[10px] font-bold animate-pulse" data-testid="se-pending-bg">
              ⚠ Foto nuova non salvata
            </div>
          )}
          {bgImage && (bgImage.naturalWidth !== canvasConfig.width || bgImage.naturalHeight !== canvasConfig.height) && (
            <button onClick={() => {
              const newW = bgImage.naturalWidth;
              const newH = bgImage.naturalHeight;
              const sx = newW / canvasConfig.width;
              const sy = newH / canvasConfig.height;
              if (!window.confirm(`Canvas attuale: ${canvasConfig.width}×${canvasConfig.height}\nFoto base: ${newW}×${newH}\n\nVuoi allineare il canvas alla foto?\nI layer esistenti verranno scalati di fattore ${sx.toFixed(2)}×${sy.toFixed(2)}.`)) return;
              setCanvasConfig({ width: newW, height: newH, aspect: newW === newH ? "square" : (newW > newH ? "landscape" : "portrait") });
              // Scala tutti i path SVG dei layer
              setLayers((ls) => ls.map((l) => {
                if (!l.svg_path) return l;
                const scaled = l.svg_path.replace(/(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)/g, (_, x, y) => `${(parseFloat(x) * sx).toFixed(2)} ${(parseFloat(y) * sy).toFixed(2)}`);
                return { ...l, svg_path: scaled, bbox_norm: l.bbox_norm };
              }));
              toast.success(`Canvas allineato a ${newW}×${newH}. Salva il blueprint per persistere.`);
            }} title="Allinea canvas e layer alla risoluzione naturale della foto"
              className="h-9 px-3 rounded-sm border border-amber-400 bg-amber-50 text-amber-800 text-xs inline-flex items-center gap-1.5 hover:bg-amber-100" data-testid="se-align-canvas">
              ⚠️ Allinea canvas ({canvasConfig.width}×{canvasConfig.height} → {bgImage.naturalWidth}×{bgImage.naturalHeight})
            </button>
          )}
          <button onClick={() => setOnionSkin((v) => !v)} className="h-9 px-3 rounded-sm border border-border hover:bg-accent text-xs inline-flex items-center gap-1.5" data-testid="se-onion-skin">
            {onionSkin ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} Onion skin
          </button>
          <button onClick={aiDetectLayers} disabled={saving} title="L'AI analizza la foto base e propone i layer automaticamente"
            className="h-9 px-3 rounded-sm border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs inline-flex items-center gap-1.5"
            data-testid="se-ai-detect">
            <Bot className="w-3.5 h-3.5" /> Rileva AI
          </button>
          <button onClick={generateShadingMap} disabled={saving} title="Genera la mappa luci/ombre per il configuratore foto-realistico"
            className="h-9 px-3 rounded-sm border border-purple-300 text-purple-700 hover:bg-purple-50 text-xs inline-flex items-center gap-1.5"
            data-testid="se-gen-shading">
            <Sparkles className="w-3.5 h-3.5" /> Genera shading map
          </button>
          <button onClick={saveBlueprint} disabled={saving} className="h-9 px-4 rounded-sm bg-brand text-white text-xs font-medium hover:bg-brand-hover disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="se-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Salva blueprint
          </button>
        </div>

        <div className="flex-1 flex">
          {/* Left toolbar */}
          <div className="w-16 border-r border-border bg-neutral-50 py-3 flex flex-col items-center gap-1" data-testid="se-toolbar">
            {TOOLS.map((t) => (
              <button key={t.id} onClick={() => { setTool(t.id); setBezierNodes([]); setDrawingPoints([]); setMarquee(null); }} title={t.label} data-testid={`se-tool-${t.id}`}
                className={`w-10 h-10 rounded-sm inline-flex items-center justify-center transition-colors ${tool === t.id ? "bg-brand text-white" : "hover:bg-accent text-muted-foreground"}`}>
                <t.icon className="w-4 h-4" />
              </button>
            ))}
            <div className="w-8 h-px bg-border my-2" />
            <button onClick={undo} disabled={historyPast.length === 0} title="Annulla (Ctrl+Z)" data-testid="se-undo"
              className="w-10 h-10 hover:bg-accent disabled:opacity-30 rounded-sm inline-flex items-center justify-center text-muted-foreground">
              <Undo2 className="w-4 h-4" />
            </button>
            <button onClick={redo} disabled={historyFuture.length === 0} title="Ripristina (Ctrl+Shift+Z)" data-testid="se-redo"
              className="w-10 h-10 hover:bg-accent disabled:opacity-30 rounded-sm inline-flex items-center justify-center text-muted-foreground">
              <Redo2 className="w-4 h-4" />
            </button>
            <div className="w-8 h-px bg-border my-2" />
            <button onClick={() => setZoom((z) => Math.min(2, z + 0.1))} className="w-10 h-10 hover:bg-accent rounded-sm inline-flex items-center justify-center text-muted-foreground" data-testid="se-zoom-in">
              <ZoomIn className="w-4 h-4" />
            </button>
            <button onClick={() => setZoom((z) => Math.max(0.2, z - 0.1))} className="w-10 h-10 hover:bg-accent rounded-sm inline-flex items-center justify-center text-muted-foreground" data-testid="se-zoom-out">
              <ZoomOut className="w-4 h-4" />
            </button>
            {tool === "wand" && (
              <div className="mt-3 w-12">
                <div className="text-[9px] text-center text-muted-foreground mb-1">Tol {tolerance}</div>
                <input type="range" min="5" max="80" value={tolerance} onChange={(e) => setTolerance(Number(e.target.value))} className="w-full" data-testid="se-tolerance" />
              </div>
            )}
          </div>

          {/* Canvas */}
          <div className="flex-1 bg-neutral-100 overflow-auto p-6 flex items-start justify-center">
            <div className="relative bg-white shadow-lg" style={{ width: canvasConfig.width * zoom, height: canvasConfig.height * zoom }}>
              {bgImage && bgConfig.visible && bgConfig.opacity > 0 && (() => {
                const s = bgConfig.scale || 1;
                const ox = (bgConfig.offset_x || 0) * zoom;
                const oy = (bgConfig.offset_y || 0) * zoom;
                return (
                  <img src={bgImage.src} alt="" draggable={false}
                    className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                    style={{
                      transform: `translate(${ox}px, ${oy}px) scale(${s})`,
                      transformOrigin: "center",
                      opacity: bgConfig.opacity ?? 1,
                      mixBlendMode: bgConfig.blend_mode || "normal",
                    }} />
                );
              })()}
              <svg
                ref={canvasRef}
                onClick={onCanvasClick}
                onMouseMove={onCanvasMouseMove}
                onMouseDown={onCanvasMouseDown}
                onMouseUp={onCanvasMouseUp}
                onMouseLeave={onCanvasMouseUp}
                onDoubleClick={finishPolygon}
                viewBox={`0 0 ${canvasConfig.width} ${canvasConfig.height}`}
                className={`absolute inset-0 w-full h-full select-none ${tool === "move" ? "cursor-move" : "cursor-crosshair"}`}
                data-testid="se-canvas"
              >
                {/* Existing layers */}
                {layers.filter((l) => l.visible !== false && (onionSkin || selectedIds.has(l.id))).map((l) => {
                  const isSelected = selectedIds.has(l.id);
                  const isInGroup = !!l.group_id;
                  if (l.image_layer && l.image_url) {
                    const imgHref = l.image_url.startsWith("http") ? l.image_url : `${process.env.REACT_APP_BACKEND_URL || ""}${l.image_url}`;
                    return (
                      <g key={l.id} style={{ mixBlendMode: l.blend_mode || "normal", opacity: l.opacity ?? 1 }}>
                        <image href={imgHref} x={l.image_x} y={l.image_y} width={l.image_w} height={l.image_h}
                          preserveAspectRatio={l.layer_type === "shading" ? "none" : "xMidYMid meet"} data-testid={`se-layer-img-${l.id}`} />
                        {isSelected && (
                          <rect x={l.image_x} y={l.image_y} width={l.image_w} height={l.image_h}
                            fill="none" stroke={isInGroup ? "#a855f7" : "#0ea5e9"} strokeWidth={2 / zoom}
                            strokeDasharray={`${6 / zoom},${3 / zoom}`} />
                        )}
                      </g>
                    );
                  }
                  return (
                    <g key={l.id}>
                      <path d={l.svg_path} fill={l.color}
                        fillOpacity={isSelected ? 0.55 : 0.2}
                        stroke={isSelected ? (isInGroup ? "#a855f7" : "#0ea5e9") : l.color}
                        strokeWidth={isSelected ? 3 / zoom : 2 / zoom}
                        strokeDasharray={isSelected ? `${6 / zoom},${3 / zoom}` : "none"}
                        data-testid={`se-path-${l.id}`} />
                    </g>
                  );
                })}

                {/* PEN drawing in progress */}
                {tool === "pen" && drawingPoints.length > 0 && (
                  <g>
                    <polyline points={drawingPoints.map((p) => `${p.x},${p.y}`).join(" ")} fill="none" stroke="#000" strokeWidth={2 / zoom} strokeDasharray={`${4 / zoom},${2 / zoom}`} />
                    {drawingPoints.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={4 / zoom} fill="#000" />)}
                  </g>
                )}

                {/* BEZIER drawing in progress */}
                {tool === "bezier" && bezierNodes.length > 0 && (
                  <g>
                    <path d={bezierNodesToPath(bezierNodes, false)} fill="none" stroke="#0ea5e9" strokeWidth={2 / zoom} strokeDasharray={`${4 / zoom},${2 / zoom}`} />
                    {bezierNodes.map((n, i) => (
                      <g key={i}>
                        {n.hIn && <line x1={n.x} y1={n.y} x2={n.hIn.x} y2={n.hIn.y} stroke="#0ea5e9" strokeWidth={1 / zoom} opacity="0.6" />}
                        {n.hOut && <line x1={n.x} y1={n.y} x2={n.hOut.x} y2={n.hOut.y} stroke="#0ea5e9" strokeWidth={1 / zoom} opacity="0.6" />}
                        {n.hIn && <circle cx={n.hIn.x} cy={n.hIn.y} r={5 / zoom} fill="#fff" stroke="#0ea5e9" strokeWidth={1.5 / zoom} />}
                        {n.hOut && <circle cx={n.hOut.x} cy={n.hOut.y} r={5 / zoom} fill="#fff" stroke="#0ea5e9" strokeWidth={1.5 / zoom} />}
                        <rect x={n.x - 5 / zoom} y={n.y - 5 / zoom} width={10 / zoom} height={10 / zoom}
                          fill={i === 0 && bezierNodes.length >= 2 ? "#22c55e" : "#0ea5e9"} stroke="#fff" strokeWidth={1.5 / zoom} />
                      </g>
                    ))}
                  </g>
                )}

                {/* Vertex handles per modifica punti (Move tool + 1 layer selezionato) */}
                {tool === "move" && selectedIds.size === 1 && (() => {
                  const layer = layers.find((l) => l.id === [...selectedIds][0]);
                  if (!layer || layer.image_layer) return null;
                  const { points } = pathEditablePoints(layer.svg_path);
                  return (
                    <g data-testid="se-vertex-handles">
                      {points.map((p, i) => {
                        const isAnchor = p.type === "anchor";
                        const isHover = hoverVertex && hoverVertex.layerId === layer.id && hoverVertex.cmdIdx === p.cmdIdx && hoverVertex.numIdx === p.numIdx;
                        const r = (isHover ? 8 : 6) / zoom;
                        if (isAnchor) {
                          return (
                            <rect key={i} x={p.x - r} y={p.y - r} width={r * 2} height={r * 2}
                              fill="#fff" stroke="#0ea5e9" strokeWidth={2 / zoom}
                              style={{ cursor: "crosshair" }}
                              data-testid={`se-vertex-${p.cmdIdx}-${p.numIdx}`} />
                          );
                        }
                        return (
                          <circle key={i} cx={p.x} cy={p.y} r={r * 0.75}
                            fill="#fbbf24" stroke="#fff" strokeWidth={1.5 / zoom}
                            style={{ cursor: "crosshair" }}
                            data-testid={`se-vertex-${p.cmdIdx}-${p.numIdx}`} />
                        );
                      })}
                    </g>
                  );
                })()}

                {/* Marquee box */}
                {marquee && (
                  <rect
                    x={Math.min(marquee.x0, marquee.x1)} y={Math.min(marquee.y0, marquee.y1)}
                    width={Math.abs(marquee.x1 - marquee.x0)} height={Math.abs(marquee.y1 - marquee.y0)}
                    fill="rgba(14,165,233,0.15)" stroke="#0ea5e9" strokeWidth={1 / zoom}
                    strokeDasharray={`${4 / zoom},${2 / zoom}`} data-testid="se-marquee" />
                )}

                {/* Hover snap (pen) */}
                {hoverPoint && tool === "pen" && (
                  <circle cx={hoverPoint.x} cy={hoverPoint.y} r={6 / zoom} fill="none" stroke="#ef4444" strokeWidth={2 / zoom} />
                )}
              </svg>

              {!bgImage && (
                <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm pointer-events-none">
                  <div className="text-center">
                    <Upload className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    Carica una foto scocca per iniziare<br />
                    <span className="text-[11px]">oppure usa <strong>Template rapidi</strong> · <strong>Ctrl+V</strong> per incollare uno screenshot</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right panel: layers + sfondo */}
          <div className="w-72 border-l border-border bg-white flex flex-col" data-testid="se-layers">
            {/* SFONDO panel — gestione foto base come layer */}
            <div className="px-3 py-2.5 border-b border-border bg-neutral-50" data-testid="se-bg-panel">
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-[11px] uppercase tracking-wider">Sfondo (foto base)</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setBgConfig((b) => ({ ...b, visible: !b.visible }))}
                    className="p-1 text-muted-foreground hover:text-foreground"
                    title={bgConfig.visible ? "Nascondi sfondo" : "Mostra sfondo"}
                    data-testid="se-bg-toggle">
                    {bgConfig.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={clearBackground}
                    className="p-1 text-red-600 hover:text-red-700"
                    title="Cancella sfondo dalla scena"
                    data-testid="se-bg-clear">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1.5 mb-1.5">
                <div>
                  <label className="text-[9px] uppercase tracking-wider text-muted-foreground flex justify-between"><span>Zoom</span><span>{Math.round(bgConfig.scale * 100)}%</span></label>
                  <input type="range" min="0.3" max="2" step="0.05" value={bgConfig.scale}
                    onChange={(e) => setBgConfig((b) => ({ ...b, scale: Number(e.target.value) }))}
                    className="w-full" data-testid="se-bg-scale" />
                </div>
                <div>
                  <label className="text-[9px] uppercase tracking-wider text-muted-foreground flex justify-between"><span>Opacità</span><span>{Math.round(bgConfig.opacity * 100)}%</span></label>
                  <input type="range" min="0" max="1" step="0.05" value={bgConfig.opacity}
                    onChange={(e) => setBgConfig((b) => ({ ...b, opacity: Number(e.target.value) }))}
                    className="w-full" data-testid="se-bg-opacity" />
                </div>
                <div>
                  <label className="text-[9px] uppercase tracking-wider text-muted-foreground">Offset X</label>
                  <input type="number" value={bgConfig.offset_x}
                    onChange={(e) => setBgConfig((b) => ({ ...b, offset_x: Number(e.target.value) }))}
                    className="w-full h-7 text-[11px] border border-border rounded-sm px-1.5" data-testid="se-bg-ox" />
                </div>
                <div>
                  <label className="text-[9px] uppercase tracking-wider text-muted-foreground">Offset Y</label>
                  <input type="number" value={bgConfig.offset_y}
                    onChange={(e) => setBgConfig((b) => ({ ...b, offset_y: Number(e.target.value) }))}
                    className="w-full h-7 text-[11px] border border-border rounded-sm px-1.5" data-testid="se-bg-oy" />
                </div>
              </div>
              {/* Blend mode sfondo */}
              <div className="mb-1.5">
                <label className="text-[9px] uppercase tracking-wider text-muted-foreground">Blend mode sfondo</label>
                <select value={bgConfig.blend_mode || "normal"} onChange={(e) => setBgConfig((b) => ({ ...b, blend_mode: e.target.value }))}
                  className="w-full h-7 text-[11px] border border-border rounded-sm px-1.5" data-testid="se-bg-blend">
                  {BLEND_MODES.filter((b) => b.id !== "auto").map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
                </select>
              </div>
              {/* Dimensioni scena (canvas) manuali */}
              <div className="border-t border-border pt-1.5 mt-1.5">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">Dimensioni scena (px)</label>
                  <label className="flex items-center gap-1 text-[9px] cursor-pointer" title="Se attivo, caricando una nuova foto le dimensioni del canvas NON vengono sovrascritte">
                    <input type="checkbox" checked={!!bgConfig.lock_canvas}
                      onChange={(e) => setBgConfig((b) => ({ ...b, lock_canvas: e.target.checked }))}
                      data-testid="se-bg-lock" />
                    <span>Mantieni</span>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <div>
                    <label className="text-[9px] text-muted-foreground">Larghezza</label>
                    <input type="number" min="100" value={canvasConfig.width}
                      onChange={(e) => setCanvasConfig((c) => ({ ...c, width: Math.max(100, Number(e.target.value) || 100) }))}
                      className="w-full h-7 text-[11px] border border-border rounded-sm px-1.5" data-testid="se-canvas-w" />
                  </div>
                  <div>
                    <label className="text-[9px] text-muted-foreground">Altezza</label>
                    <input type="number" min="100" value={canvasConfig.height}
                      onChange={(e) => setCanvasConfig((c) => ({ ...c, height: Math.max(100, Number(e.target.value) || 100) }))}
                      className="w-full h-7 text-[11px] border border-border rounded-sm px-1.5" data-testid="se-canvas-h" />
                  </div>
                </div>
                <div className="flex gap-1 mt-1 flex-wrap">
                  {[[1200, 1200, "Quadrato"], [1200, 1500, "4:5"], [1500, 1200, "5:4"], [1920, 1080, "16:9"]].map(([w, h, lbl]) => (
                    <button key={lbl} onClick={() => setCanvasConfig({ width: w, height: h, aspect: w === h ? "square" : (w > h ? "landscape" : "portrait") })}
                      className="text-[9px] px-1.5 py-0.5 rounded-sm border border-border hover:bg-accent" data-testid={`se-preset-${w}x${h}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={() => setBgConfig({ scale: 1, offset_x: 0, offset_y: 0, opacity: 1, visible: true, blend_mode: "normal", lock_canvas: bgConfig.lock_canvas })}
                className="mt-1.5 text-[10px] text-muted-foreground hover:text-foreground underline" data-testid="se-bg-reset">
                Reset sfondo
              </button>
            </div>

            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
              <Layers className="w-4 h-4" />
              <span className="font-bold text-sm">Layer ({layers.length})</span>
              {selectedIds.size > 0 && (
                <span className="text-[10px] text-brand ml-auto" data-testid="se-sel-count">{selectedIds.size} sel.</span>
              )}
            </div>
            <div className="flex-1 overflow-auto">
              {layers.length === 0 ? (
                <div className="p-4 text-xs text-muted-foreground text-center">
                  {tool === "pen" ? "Click sulla foto per aggiungere punti, Enter per chiudere." :
                   tool === "bezier" ? "Click = ancora · Click+trascina = curva Bezier · Click sul nodo verde = chiudi" :
                   tool === "wand" ? "Click su un'area di colore omogeneo." :
                   tool === "move" ? "Click su layer per selezionare · Shift+click = multi · trascina su area vuota = marquee" :
                   "Scegli uno strumento e disegna sulla foto."}
                </div>
              ) : (
                [...layers].sort((a, b) => b.z_index - a.z_index).map((l) => {
                  const isSel = selectedIds.has(l.id);
                  const toggleId = () => setSelectedIds((s) => {
                    const arr = [...s];
                    return new Set(arr.includes(l.id) ? arr.filter((x) => x !== l.id) : [...arr, l.id]);
                  });
                  return (
                    <div key={l.id} onClick={(e) => {
                      // Guard: ignore clicks originated on interactive children (checkbox/input/button)
                      if (e.target.closest("input,button")) return;
                      const shift = e.shiftKey || e.nativeEvent?.shiftKey;
                      if (shift) {
                        toggleId();
                      } else {
                        const ids = l.group_id ? layers.filter((x) => x.group_id === l.group_id).map((x) => x.id) : [l.id];
                        setSelectedIds(new Set(ids));
                      }
                    }}
                      className={`px-3 py-2.5 border-b border-border cursor-pointer transition-colors ${isSel ? "bg-brand/10" : "hover:bg-accent"}`}
                      data-testid={`se-layer-${l.id}`}>
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={isSel}
                          onChange={(e) => { e.stopPropagation(); toggleId(); }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-3 h-3" data-testid={`se-chk-${l.id}`} />
                        <span className="w-3 h-3 rounded-sm border border-border" style={{ background: l.color }} />
                        {renameId === l.id ? (
                          <input value={l.name} onChange={(e) => updateLayer(l.id, { name: e.target.value })}
                            onBlur={() => setRenameId(null)}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") setRenameId(null); }}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            className="flex-1 text-xs bg-white border border-brand outline-none rounded-sm px-1 min-w-0"
                            data-testid={`se-name-input-${l.id}`} />
                        ) : (
                          <span onDoubleClick={(e) => { e.stopPropagation(); setRenameId(l.id); }}
                            title="Doppio click per rinominare"
                            className="flex-1 text-xs truncate cursor-text"
                            data-testid={`se-name-${l.id}`}>{l.name}</span>
                        )}
                        {l.group_id && <span title="In gruppo" className="text-[9px] px-1 rounded bg-purple-100 text-purple-700">G</span>}
                        <button onClick={(e) => { e.stopPropagation(); updateLayer(l.id, { visible: l.visible === false ? true : false }); }} className="p-1 text-muted-foreground hover:text-foreground">
                          {l.visible === false ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); reorderLayer(l.id, "forward"); }} className="p-1 text-muted-foreground hover:text-foreground" title="Porta avanti" data-testid={`se-zfwd-${l.id}`}>
                          <ChevronUp className="w-3 h-3" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); reorderLayer(l.id, "backward"); }} className="p-1 text-muted-foreground hover:text-foreground" title="Porta indietro" data-testid={`se-zbwd-${l.id}`}>
                          <ChevronDown className="w-3 h-3" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); removeLayer(l.id); }} className="p-1 text-red-500 hover:text-red-700">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      {activeId === l.id && (
                        <div className="mt-2 space-y-2 pl-5">
                          {/* Quick z-order */}
                          <div className="flex gap-1">
                            <button onClick={() => reorderLayer(l.id, "front")} className="flex-1 h-6 text-[10px] border border-border rounded-sm hover:bg-accent" data-testid={`se-zfront-${l.id}`}>⏫ In cima</button>
                            <button onClick={() => reorderLayer(l.id, "back")} className="flex-1 h-6 text-[10px] border border-border rounded-sm hover:bg-accent" data-testid={`se-zback-${l.id}`}>⏬ In fondo</button>
                          </div>
                          <div>
                            <label className="text-[9px] uppercase tracking-wider text-muted-foreground">Gruppo superficie</label>
                            <select value={l.surface_group} onChange={(e) => updateLayerSurfaceGroup(l.id, e.target.value)}
                              className="w-full h-7 text-[11px] border border-border rounded-sm px-1.5" data-testid={`se-group-${l.id}`}>
                              {SURFACE_GROUPS.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
                            </select>
                          </div>
                          <div className="grid grid-cols-2 gap-1">
                            <div>
                              <label className="text-[9px] uppercase tracking-wider text-muted-foreground">Z-index</label>
                              <input type="number" value={l.z_index} onChange={(e) => updateLayer(l.id, { z_index: Number(e.target.value) })}
                                className="w-full h-7 text-[11px] border border-border rounded-sm px-1.5" />
                            </div>
                            <div>
                              <label className="text-[9px] uppercase tracking-wider text-muted-foreground">Scale</label>
                              <input type="number" step="0.1" value={l.anchor?.scale || 1} onChange={(e) => updateLayer(l.id, { anchor: { ...l.anchor, scale: Number(e.target.value) } })}
                                className="w-full h-7 text-[11px] border border-border rounded-sm px-1.5" />
                            </div>
                          </div>
                          {/* Blend mode (come Photoshop) */}
                          <div>
                            <label className="text-[9px] uppercase tracking-wider text-muted-foreground">Blend mode</label>
                            <select value={l.blend_mode || "auto"} onChange={(e) => updateLayer(l.id, { blend_mode: e.target.value === "auto" ? null : e.target.value })}
                              className="w-full h-7 text-[11px] border border-border rounded-sm px-1.5" data-testid={`se-blend-${l.id}`}>
                              {BLEND_MODES.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
                            </select>
                          </div>
                          {/* Opacità layer */}
                          <div>
                            <label className="text-[9px] uppercase tracking-wider text-muted-foreground flex justify-between"><span>Opacità</span><span>{Math.round((l.opacity ?? 1) * 100)}%</span></label>
                            <input type="range" min="0" max="1" step="0.05" value={l.opacity ?? 1}
                              onChange={(e) => updateLayer(l.id, { opacity: Number(e.target.value) })}
                              className="w-full" data-testid={`se-opacity-${l.id}`} />
                          </div>

                          {/* Tipo layer: Maschera vs Contenitore PNG vs Shading map */}
                          <div className="border-t border-border pt-2 mt-2">
                            <label className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold">Tipo layer</label>
                            {l.layer_type === "shading" ? (
                              <div className="mt-1 p-2 bg-purple-50 border border-purple-200 rounded-sm text-[10px] text-purple-900 leading-tight">
                                <strong>Shading map</strong> — overlay luci/ombre PNG. Modifica blend mode/opacità sopra per integrarlo nella scena. Elimina questo layer per rimuoverlo.
                              </div>
                            ) : (
                            <>
                            <div className="grid grid-cols-2 gap-1 mt-1">
                              <button type="button" onClick={() => updateLayer(l.id, { layer_type: "mask" })}
                                className={`h-7 text-[10px] rounded-sm border transition-all ${(l.layer_type || "mask") === "mask" ? "border-brand bg-brand/10 text-brand font-bold" : "border-border hover:border-foreground"}`}
                                data-testid={`se-type-mask-${l.id}`}>Maschera</button>
                              <button type="button" onClick={() => updateLayer(l.id, { layer_type: "container", container_config: l.container_config || { anchor_x: canvasConfig.width / 2, anchor_y: canvasConfig.height / 2, bbox: [canvasConfig.width * 0.35, canvasConfig.height * 0.6, canvasConfig.width * 0.65, canvasConfig.height * 0.95], library_axis: "gambe" } })}
                                className={`h-7 text-[10px] rounded-sm border transition-all ${l.layer_type === "container" ? "border-brand bg-brand/10 text-brand font-bold" : "border-border hover:border-foreground"}`}
                                data-testid={`se-type-container-${l.id}`}>Contenitore PNG</button>
                            </div>
                            {l.layer_type === "container" && (
                              <div className="mt-2 space-y-1.5 p-2 bg-amber-50 border border-amber-200 rounded-sm">
                                <div>
                                  <label className="text-[9px] text-muted-foreground">Asse libreria</label>
                                  <select value={l.container_config?.library_axis || "gambe"}
                                    onChange={(e) => updateLayer(l.id, { container_config: { ...l.container_config, library_axis: e.target.value } })}
                                    className="w-full h-7 text-[11px] border border-border rounded-sm px-1.5" data-testid={`se-axis-${l.id}`}>
                                    <option value="gambe">Gambe</option>
                                    <option value="piedi">Piedi</option>
                                    <option value="metalli">Metalli</option>
                                    <option value="piani">Piani</option>
                                    <option value="basi">Basi</option>
                                    <option value="cornici">Cornici</option>
                                    <option value="paralumi">Paralumi</option>
                                  </select>
                                </div>
                                <div className="grid grid-cols-2 gap-1">
                                  <div>
                                    <label className="text-[9px] text-muted-foreground">BBox X0</label>
                                    <input type="number" value={l.container_config?.bbox?.[0] || 0}
                                      onChange={(e) => { const bb = [...(l.container_config?.bbox || [0,0,0,0])]; bb[0] = Number(e.target.value); updateLayer(l.id, { container_config: { ...l.container_config, bbox: bb } }); }}
                                      className="w-full h-7 text-[11px] border border-border rounded-sm px-1.5" />
                                  </div>
                                  <div>
                                    <label className="text-[9px] text-muted-foreground">BBox Y0</label>
                                    <input type="number" value={l.container_config?.bbox?.[1] || 0}
                                      onChange={(e) => { const bb = [...(l.container_config?.bbox || [0,0,0,0])]; bb[1] = Number(e.target.value); updateLayer(l.id, { container_config: { ...l.container_config, bbox: bb } }); }}
                                      className="w-full h-7 text-[11px] border border-border rounded-sm px-1.5" />
                                  </div>
                                  <div>
                                    <label className="text-[9px] text-muted-foreground">BBox X1</label>
                                    <input type="number" value={l.container_config?.bbox?.[2] || 0}
                                      onChange={(e) => { const bb = [...(l.container_config?.bbox || [0,0,0,0])]; bb[2] = Number(e.target.value); updateLayer(l.id, { container_config: { ...l.container_config, bbox: bb } }); }}
                                      className="w-full h-7 text-[11px] border border-border rounded-sm px-1.5" />
                                  </div>
                                  <div>
                                    <label className="text-[9px] text-muted-foreground">BBox Y1</label>
                                    <input type="number" value={l.container_config?.bbox?.[3] || 0}
                                      onChange={(e) => { const bb = [...(l.container_config?.bbox || [0,0,0,0])]; bb[3] = Number(e.target.value); updateLayer(l.id, { container_config: { ...l.container_config, bbox: bb } }); }}
                                      className="w-full h-7 text-[11px] border border-border rounded-sm px-1.5" />
                                  </div>
                                </div>
                                <div className="text-[9px] text-amber-900 leading-tight">
                                  Il PNG dell&apos;item scelto nella libreria verrà posizionato in questo rettangolo nel B2C.
                                </div>
                              </div>
                            )}
                            </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            <div className="p-3 border-t border-border bg-neutral-50 text-[10px] text-muted-foreground space-y-0.5 leading-snug">
              <div>✏️ <strong>Move</strong>: drag layer · Shift+click multi · marquee · <strong>vertici visibili</strong> con 1 layer sel.</div>
              <div>⌨️ <strong>Frecce</strong>: muovi layer di 1px (10px con Shift)</div>
              <div>📦 <strong>Ctrl+G</strong> raggruppa · <strong>Ctrl+Shift+G</strong> sgruppa</div>
              <div>📋 <strong>Ctrl+C</strong>/<strong>V</strong> copia/incolla · <strong>Ctrl+A</strong> tutti · <strong>Del</strong> elimina</div>
              <div>🖼️ <strong>Ctrl+V</strong> su screenshot = paste immagine</div>
              <div>⏪ <strong>Ctrl+Z</strong>/<strong>Shift+Z</strong> undo/redo ({HISTORY_MAX} step)</div>
            </div>
          </div>
        </div>

        {/* Shading map modal */}
        {showShadingModal && (
          <ShadingMapModal productId={productId} productName={product?.nome}
            bgImageUrl={currentBgUrl}
            onClose={() => setShowShadingModal(false)}
            onCreateLayer={handleCreateShadingLayer} />
        )}

        {/* Paste image modal */}
        {pasteImagePrompt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="se-paste-modal">
            <div className="bg-white rounded-sm p-6 max-w-md w-full mx-4 shadow-xl">
              <h3 className="text-lg font-bold mb-2">Immagine incollata dal clipboard</h3>
              <p className="text-sm text-muted-foreground mb-4">Come vuoi utilizzarla?</p>
              <img src={pasteImagePrompt.dataUrl} alt="" className="w-full max-h-48 object-contain bg-neutral-100 mb-4" />
              <div className="flex flex-col gap-2">
                <button onClick={acceptPasteAsBackground} data-testid="se-paste-bg"
                  className="w-full px-4 py-2.5 rounded-sm bg-brand text-white text-sm font-medium hover:bg-brand-hover">
                  Sostituisci foto base (sfondo)
                </button>
                <button onClick={acceptPasteAsLayer} data-testid="se-paste-layer"
                  className="w-full px-4 py-2.5 rounded-sm border border-border text-sm font-medium hover:bg-accent">
                  Inserisci come nuovo layer immagine
                </button>
                <button onClick={() => setPasteImagePrompt(null)} className="w-full px-4 py-2 text-sm text-muted-foreground hover:text-foreground">
                  Annulla
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
